import { useState } from "react";
import { AlertTriangle, Loader2, Upload } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { importQboAgingCsv, type QboAgingImportResult, type QboAgingLedger } from "./qboAgingApi";

const AP_INSTRUCTIONS = [
  "In QuickBooks Online: Reports → What you owe → A/P Aging Detail.",
  "Export to CSV, then choose the file below and upload.",
  "Rows match hub GIPI and TSH purchase invoices by document number. Open balances on the report limit GIPI/TSH outflows on Cash flow forecast.",
].join(" ");

const AR_INSTRUCTIONS = [
  "In QuickBooks Online: Reports → Who owes you → A/R Aging Detail.",
  "Export to CSV, then choose the file below and upload.",
  "Rows match hub GOSI sales invoices by document number. Open balances on the report limit GOSI inflows on Cash flow forecast.",
].join(" ");

export function QboAgingImportModal({
  ledger,
  onClose,
  onImported,
}: {
  ledger: QboAgingLedger;
  onClose: () => void;
  onImported?: (result: QboAgingImportResult) => void;
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<QboAgingImportResult | null>(null);

  const title = ledger === "ap" ? "Import AP aging" : "Import AR aging";

  async function handleUpload(): Promise<void> {
    if (!file) {
      setError("Choose a CSV file first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const imported = await importQboAgingCsv(ledger, file);
      setResult(imported);
      onImported?.(imported);
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : String(uploadError);
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <DocumentModal
      title={title}
      headerExtra={ledger === "ap" ? AP_INSTRUCTIONS : AR_INSTRUCTIONS}
      onClose={onClose}
      shellClassName="max-w-lg"
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor={`qbo-aging-file-${ledger}`}>Aging detail CSV</Label>
          <Input
            id={`qbo-aging-file-${ledger}`}
            type="file"
            accept=".csv,text/csv"
            className="mt-1"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setError(null);
              setResult(null);
            }}
          />
        </div>

        {error ? (
          <p className="flex items-start gap-2 text-sm text-rose-700" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}

        {result ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 space-y-1">
            <p className="font-medium text-slate-900">Import saved</p>
            <p>
              Matched {result.summary.matched} · QBO only {result.summary.qboOnly} · Supabase only{" "}
              {result.summary.supabaseOnly}
            </p>
            {ledger === "ap" && result.ap_aging_filter ? (
              <p>
                Cash flow forecast now uses {result.ap_aging_filter.open_gipi_tsh_count} open GIPI/TSH bills from this
                report.
              </p>
            ) : null}
            {ledger === "ar" && result.ar_aging_filter ? (
              <p>
                Cash flow forecast now uses {result.ar_aging_filter.open_gosi_count} open GOSI invoices from this
                report.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {result ? "Done" : "Cancel"}
          </Button>
          {!result ? (
            <Button type="button" disabled={loading || !file} onClick={() => void handleUpload()}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" aria-hidden />
                  Upload and match
                </>
              )}
            </Button>
          ) : null}
        </div>
      </div>
    </DocumentModal>
  );
}
